import { Endpoints } from "../lib/endpoints";
import { PerformRequest } from "../lib/usePerformRequest";
import { NonPaginatedResponse, PaginatedResponse } from "../lib/types";

export type UwipPlan = "monthly" | "annual";
export type UwipMembershipStatus =
  | "checkout_pending"
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "zelle_pending";

export interface IUwipPayment {
  stripeInvoiceId?: string;
  amountCents: number;
  status: "succeeded" | "failed" | "pending";
  paidAt?: number;
  description?: string;
}

export interface IUwipMembership {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthYear?: number;
  preferredContactTime?: string;
  plan: UwipPlan;
  status: UwipMembershipStatus;
  paymentMethod: "stripe" | "zelle";
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  isFoundingMember: boolean;
  foundingNumber?: number;
  consentVersion: string;
  consentAcceptedAt?: number;
  trialEndsAt?: number;
  currentPeriodEnd?: number;
  cancelAtPeriodEnd?: boolean;
  canceledAt?: number;
  payments?: IUwipPayment[];
  createdAt: number;
}

export interface UwipMembershipList {
  data: IUwipMembership[];
  totalRecords: number;
  limit: number;
  page: number;
  foundingRemaining?: number;
  foundingUsed?: number;
  foundingCap?: number;
}

export async function getUwipMemberships(
  page: number = 1,
  limit: number = 10,
  status?: string
): Promise<UwipMembershipList> {
  const result = await PerformRequest<
    PaginatedResponse<IUwipMembership[]> & {
      meta?: {
        foundingRemaining: number;
        foundingUsed: number;
        foundingCap: number;
      };
    }
  >({
    method: Endpoints.GetUwipMemberships(page, limit, status).method,
    route: Endpoints.GetUwipMemberships(page, limit, status).route,
  });

  if (result.status === 200 && result.data) {
    return {
      data: result.data.data || [],
      totalRecords: result.data.totalRecords,
      limit: result.data.limit,
      page: result.data.page,
      foundingRemaining: result.data.meta?.foundingRemaining,
      foundingUsed: result.data.meta?.foundingUsed,
      foundingCap: result.data.meta?.foundingCap,
    };
  }

  throw new Error(result.message || "Failed to fetch U-WIP memberships");
}

export async function getUwipMembershipById(
  id: string
): Promise<IUwipMembership> {
  const result = await PerformRequest<NonPaginatedResponse<IUwipMembership>>({
    method: Endpoints.GetUwipMembershipById(id).method,
    route: Endpoints.GetUwipMembershipById(id).route,
  });

  if (result.status === 200 && result.data?.data) {
    return result.data.data;
  }

  throw new Error(result.message || "Failed to fetch membership");
}

export async function cancelUwipMembership(
  id: string
): Promise<IUwipMembership> {
  const result = await PerformRequest<NonPaginatedResponse<IUwipMembership>>({
    method: Endpoints.CancelUwipMembership(id).method,
    route: Endpoints.CancelUwipMembership(id).route,
  });

  if (result.status === 200 && result.data?.data) {
    return result.data.data;
  }

  throw new Error(result.message || "Failed to cancel membership");
}

export async function markUwipZellePaid(
  id: string
): Promise<IUwipMembership> {
  const result = await PerformRequest<NonPaginatedResponse<IUwipMembership>>({
    method: Endpoints.MarkUwipZellePaid(id).method,
    route: Endpoints.MarkUwipZellePaid(id).route,
  });

  if (result.status === 200 && result.data?.data) {
    return result.data.data;
  }

  throw new Error(result.message || "Failed to confirm Zelle payment");
}
